import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, currency, formatApiError } from "../lib/api";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../lib/auth";
import { ProductCard } from "../components/Cards";
import { Star, ShoppingCart, Heart, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "e-file", label: "E-Files" },
  { key: "printed-product", label: "3D Printed Products" },
  { key: "printer", label: "3D Printers" },
  { key: "filament", label: "Filaments" },
  { key: "resin", label: "Resins" },
  { key: "equipment", label: "Equipment" },
];

export function Shop() {
  const { category: routeCat } = useParams();
  const [category, setCategory] = useState(routeCat || "all");
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  const catKeyMap = { "e-files": "e-file", "printed-products": "printed-product", "printers": "printer", "filaments": "filament", "resins": "resin", "equipment": "equipment" };
  useEffect(() => { if (routeCat) setCategory(catKeyMap[routeCat] || routeCat); }, [routeCat]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ sort });
    if (category !== "all") p.set("category", category);
    if (q) p.set("q", q);
    api.get(`/products?${p}`).then(r => { setProducts(r.data); setLoading(false); });
  }, [category, q, sort]);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="tl-label mb-2">Shop</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold">Discover Products & Files</h1>
      </div>

      <div className="flex overflow-x-auto no-scrollbar gap-2 mb-6 border-b border-slate-200 pb-4">
        {CATEGORIES.map(c => (
          <button key={c.key} data-testid={`shop-cat-${c.key}`} onClick={() => setCategory(c.key)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${category === c.key ? "bg-[#2563EB] border-[#2563EB] text-slate-900" : "border-slate-200 text-[#475569] hover:border-white/20"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="tl-input pl-10" />
        </div>
        <select data-testid="shop-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="tl-input md:max-w-xs">
          <option value="featured">Featured</option>
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="rating">Top Rated</option>
        </select>
      </div>

      {loading ? <div className="text-center py-20 text-[#475569]">Loading products…</div> : products.length === 0 ? (
        <div data-testid="no-products" className="text-center py-20"><div className="text-2xl font-display font-semibold mb-2">No products found</div><div className="text-[#475569]">Try different filters.</div></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{products.map(p => <ProductCard key={p.id} product={p} />)}</div>
      )}
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/products/${slug}`).then(r => setP(r.data)).catch(() => setP(false)); }, [slug]);

  const addCart = async () => {
    if (!user || user === false) { nav("/login"); return; }
    try { await api.post("/cart/add", { product_id: p.id, quantity: 1 }); toast.success("Added to cart"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (p === null) return <div className="py-20 text-center text-[#475569]">Loading…</div>;
  if (p === false) return <div className="py-20 text-center">Product not found.</div>;

  return (
    <div data-testid="product-detail-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="tl-card overflow-hidden">
          <img src={(p.images || [])[0]} alt={p.name} className="w-full aspect-square object-cover" />
        </div>
        <div>
          <div className="tl-label mb-2">{p.category}</div>
          <h1 className="text-3xl font-display font-bold mb-2">{p.name}</h1>
          <div className="text-sm text-[#475569] mb-4">by <Link to="#" className="text-[#0891B2]">{p.creator_name}</Link></div>
          <div className="flex items-center gap-4 mb-6 text-sm">
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#0891B2] text-[#0891B2]" />{p.rating_average?.toFixed(1)} ({p.review_count})</span>
            {p.stock > 0 && <span className="text-[#0891B2]">In Stock</span>}
          </div>
          <div className="font-display text-4xl font-bold mb-6">{p.is_free ? <span className="text-[#0891B2]">FREE</span> : currency(p.price)}</div>
          <p className="text-[#475569] leading-relaxed mb-6">{p.description}</p>
          <div className="flex gap-3 mb-6">
            <button data-testid="add-to-cart" onClick={addCart} className="tl-btn-primary flex-1 justify-center"><ShoppingCart className="w-4 h-4" /> Add to Cart</button>
            <button data-testid="wishlist-btn" onClick={() => api.post("/wishlist/toggle", { kind: "products", id: p.id }).then(() => toast.success("Saved"))} className="tl-btn-ghost"><Heart className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-2">{(p.tags || []).map(t => <span key={t} className="tl-chip">{t}</span>)}</div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_ADDR = { full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };
const isDigitalItem = (it) => it.product?.category === "e-file" || it.product?.product_type === "digital";
const ADDR_FIELDS = [
  ["full_name", "Full name", "sm:col-span-1"], ["phone", "Mobile (10 digits)", "sm:col-span-1"],
  ["line1", "Address line 1", "sm:col-span-2"], ["line2", "Address line 2 (optional)", "sm:col-span-2"],
  ["city", "City", ""], ["state", "State", ""], ["pincode", "Pincode", ""],
];

function AddressForm({ addr, setAddr }) {
  return (
    <div data-testid="shipping-address-form" className="tl-card p-5">
      <div className="font-semibold mb-1">Shipping address</div>
      <p className="text-xs text-[#475569] mb-4">Required for physical items. Digital files are delivered instantly to your account.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {ADDR_FIELDS.map(([k, ph, cls]) => (
          <input key={k} data-testid={`addr-${k}`} placeholder={ph} value={addr[k] || ""}
            onChange={e => setAddr({ ...addr, [k]: e.target.value })} className={`tl-input ${cls}`} />
        ))}
      </div>
    </div>
  );
}

export function Cart() {
  const [cart, setCart] = useState({ items: [] });
  const { user } = useAuth();
  const nav = useNavigate();
  const load = () => api.get("/cart").then(r => setCart(r.data));
  useEffect(() => { if (user && user !== false) load(); }, [user]);

  const items = cart.items || [];
  const total = items.reduce((s, i) => s + (i.product?.price || 0) * (i.quantity || 1), 0);

  // Group items by vendor for multi-vendor preview
  const groups = items.reduce((acc, it) => {
    const vid = it.product?.vendor_id || it.product?.creator_id || "PLATFORM";
    const name = it.product?.creator_name || "Trinity Layers";
    if (!acc[vid]) acc[vid] = { vendor_id: vid, vendor_name: name, items: [], subtotal: 0, has_physical: false, has_digital: false };
    acc[vid].items.push(it);
    acc[vid].subtotal += (it.product?.price || 0) * (it.quantity || 1);
    const isDigital = isDigitalItem(it);
    if (isDigital) acc[vid].has_digital = true; else acc[vid].has_physical = true;
    return acc;
  }, {});
  const groupList = Object.values(groups);

  const remove = async (pid) => { await api.post("/cart/remove", { product_id: pid, quantity: 1 }); load(); };
  const setQty = async (pid, q) => {
    try { await api.post("/cart/update", { product_id: pid, quantity: q }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const needsAddress = groupList.some(g => g.has_physical);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  useEffect(() => { if (cart.saved_address) setAddr({ ...EMPTY_ADDR, ...cart.saved_address }); }, [cart.saved_address]);
  const checkout = async () => {
    try {
      const { data } = await api.post("/checkout", needsAddress ? { shipping_address: addr } : {});
      if (data.requires_payment) {
        try {
          await openRazorpayCheckout(data, user);
          toast.success("Payment successful — order confirmed");
          nav("/profile");
        } catch (err) {
          toast.error(err.message || "Payment cancelled");
        }
      } else {
        toast.success("Order placed!");
        nav("/profile");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><Link to="/login" className="tl-btn-primary">Login to view cart</Link></div>;

  return (
    <div data-testid="cart-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-display font-bold mb-2">Your Cart</h1>
      {items.length > 0 && (
        <p className="text-sm text-[#475569] mb-8">
          {groupList.length} vendor{groupList.length > 1 ? "s" : ""} · One payment, {groupList.length} seller order{groupList.length > 1 ? "s" : ""} created on success.
        </p>
      )}
      {items.length === 0 ? (
        <div className="text-center py-20 text-[#475569]">Your cart is empty. <Link to="/shop" className="text-[#0891B2]">Browse shop</Link></div>
      ) : (
        <div className="space-y-6">
          {groupList.map((g, gi) => (
            <div key={g.vendor_id} data-testid={`cart-vendor-${gi}`} className="tl-card p-4">
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-slate-200">
                <div>
                  <div className="tl-label">Sold by</div>
                  <div className="font-semibold">{g.vendor_name}</div>
                </div>
                <div className="text-xs text-[#475569] flex gap-1">
                  {g.has_digital && <span className="tl-chip">Digital</span>}
                  {g.has_physical && <span className="tl-chip">Physical</span>}
                </div>
              </div>
              <div className="space-y-3">
                {g.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <img src={(it.product?.images || [])[0]} alt={it.product?.name} className="w-16 h-16 rounded object-cover" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{it.product?.name}</div>
                      <div className="text-xs text-[#475569]">{it.product?.category}{isDigitalItem(it) && " · single licence"}</div>
                    </div>
                    {!isDigitalItem(it) && (
                      <div className="flex items-center gap-1" data-testid={`cart-qty-${gi}-${i}`}>
                        <button data-testid={`cart-qty-dec-${gi}-${i}`} disabled={(it.quantity || 1) <= 1} onClick={() => setQty(it.product_id, (it.quantity || 1) - 1)} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40">−</button>
                        <span data-testid={`cart-qty-val-${gi}-${i}`} className="w-8 text-center text-sm">{it.quantity || 1}</span>
                        <button data-testid={`cart-qty-inc-${gi}-${i}`} onClick={() => setQty(it.product_id, (it.quantity || 1) + 1)} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200">+</button>
                      </div>
                    )}
                    <div className="font-semibold text-sm">{currency((it.product?.price || 0) * (it.quantity || 1))}</div>
                    <button data-testid={`cart-remove-${gi}-${i}`} onClick={() => remove(it.product_id)} className="text-xs text-[#475569] hover:text-slate-900">Remove</button>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm">
                <span className="text-[#475569]">Subtotal</span>
                <span className="font-semibold">{currency(g.subtotal)}</span>
              </div>
            </div>
          ))}
          {needsAddress && <AddressForm addr={addr} setAddr={setAddr} />}
          <div className="tl-card p-5 flex justify-between items-center">
            <div>
              <div className="text-xs text-[#475569]">Total</div>
              <div className="font-display text-2xl font-bold">{currency(total)}</div>
            </div>
            <button data-testid="checkout-btn" onClick={checkout} className="tl-btn-primary">Checkout</button>
          </div>
        </div>
      )}
    </div>
  );
}
